import { useCallback, useState } from 'react';
import { apiRequest } from '../lib/api';

/**
 * useSubmitLead — thin wrapper around the existing project apiRequest helper.
 * Provides pending / error / success state to a form component so it can
 * disable the submit control while a request is in flight.
 *
 * We reuse apiRequest (frontend/src/lib/api.js) because it already:
 *   - JSON-encodes the body,
 *   - parses JSON responses,
 *   - throws structured errors on !response.ok,
 *   - handles network errors uniformly across the app.
 * No native fetch is introduced.
 */
const useSubmitLead = (endpoint) => {
  const [status, setStatus] = useState('idle'); // idle | pending | success | error
  const [error, setError] = useState('');
  const [response, setResponse] = useState(null);

  const submit = useCallback(async (payload) => {
    if (status === 'pending') return null;   // double-click protection
    setStatus('pending');
    setError('');
    setResponse(null);
    try {
      const data = await apiRequest(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setResponse(data);
      setStatus('success');
      return data;
    } catch (exc) {
      const message = exc && exc.message ? exc.message : 'Something went wrong. Please try again.';
      setError(message);
      setStatus('error');
      return null;
    }
  }, [endpoint, status]);

  const reset = useCallback(() => {
    setStatus('idle');
    setError('');
    setResponse(null);
  }, []);

  return { submit, reset, status, error, response, pending: status === 'pending' };
};

export default useSubmitLead;
